import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, CarFront, Check, Clock3, ExternalLink, LoaderCircle, MapPin, Navigation, Phone, RefreshCw, UsersRound, X } from "lucide-react";
import GoogleMapView from "./GoogleMapView";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import ProfileAvatar from "./ProfileAvatar";

const CATALOG_PHOTO_BUCKET = "vehicle-catalog-photos";
const CITY_CENTERS = {
  tashkent: [41.2995, 69.2401], samarkand: [39.6542, 66.9597], bukhara: [39.7681, 64.4556],
  khiva: [41.3783, 60.3639], urgench: [41.55, 60.63], nukus: [42.46, 59.61],
  andijan: [40.78, 72.34], namangan: [41, 71.67], fergana: [40.39, 71.78],
  jizzakh: [40.12, 67.84], qarshi: [38.86, 65.8], termez: [37.22, 67.28],
  navoi: [40.1, 65.37], gulistan: [40.49, 68.78], shahrisabz: [39.06, 66.83],
};

function getPickupMapUrl(request) {
  const query = request.pickup_lat != null && request.pickup_lng != null
    ? `${request.pickup_lat},${request.pickup_lng}`
    : `${request.pickup_address}, ${request.from_city}`;
  const url = new URL("https://www.google.com/maps/search/");
  url.searchParams.set("api", "1");
  url.searchParams.set("query", query);
  return url.toString();
}

function displayPhone(phone) {
  return String(phone || "").replace(/[^+\d]/g, "");
}

function formatTravelAt(value, language) {
  if (!value) return "";
  const locale = language?.startsWith("uz") ? "uz-UZ" : language?.startsWith("ru") ? "ru-RU" : "en-US";
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Tashkent",
    weekday: "short",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatLocationTime(value, language) {
  if (!value) return "";
  const locale = language?.startsWith("uz") ? "uz-UZ" : language?.startsWith("ru") ? "ru-RU" : "en-US";
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Tashkent",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function RideTrackingMap({ request, driverLocationAvailable, statusLabel, statusDescription, t }) {
  const driverPosition = driverLocationAvailable ? [Number(request.driver_lat), Number(request.driver_lng)] : null;
  const pickupPosition = request.pickup_lat != null && request.pickup_lng != null
    ? [Number(request.pickup_lat), Number(request.pickup_lng)]
    : null;
  const center = driverPosition || pickupPosition || CITY_CENTERS[request.from_city] || CITY_CENTERS.tashkent;
  return (
    <div className="relative mt-4 overflow-hidden rounded-2xl border border-slate-200 shadow-lg dark:border-slate-700">
      <GoogleMapView
        center={center}
        zoom={driverPosition || pickupPosition ? 14 : 12}
        className="h-[min(52svh,440px)] min-h-[280px] w-full sm:min-h-[340px]"
        ariaLabel={t("rides.open_map")}
        fitPositions={[driverPosition, pickupPosition].filter(Boolean)}
        markers={[
          ...(driverPosition ? [{ position: driverPosition, label: t("rides.driver_location_marker"), vehicle: true }] : []),
          ...(pickupPosition ? [{ position: pickupPosition, label: t("rides.pickup"), color: "#f97316", radius: 8 }] : []),
        ]}
      />
      <div className="pointer-events-none absolute left-3 top-3 z-[500] max-w-[calc(100%-1.5rem)] rounded-xl border border-white/15 bg-slate-950/90 px-3 py-2 text-white shadow-lg backdrop-blur sm:left-4 sm:top-4 sm:max-w-sm sm:px-4 sm:py-3">
        <p className="text-xs font-extrabold sm:text-sm"><span className={`mr-2 inline-block h-2 w-2 rounded-full align-middle ${driverPosition ? "animate-pulse bg-amber-400" : "bg-slate-400"}`} />{driverPosition ? t("rides.driver_location_marker") : statusLabel}</p>
        <p className="mt-1 text-[11px] leading-4 text-slate-300 sm:text-xs">{driverPosition ? statusDescription : t("rides.driver_location_waiting")}</p>
      </div>
    </div>
  );
}

function isMissingRideSchema(error) {
  return ["42P01", "PGRST202", "PGRST203", "PGRST205", "42883"].includes(error?.code)
    || /could not find the function|does not exist|schema cache/i.test(error?.message || "");
}

export default function RideRequestsPanel({ role = "client", className = "", historyMode = false }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [workingId, setWorkingId] = useState(null);
  const [etaByRequest, setEtaByRequest] = useState({});
  const [locationShareState, setLocationShareState] = useState("idle");
  const [driverOnline, setDriverOnline] = useState(false);
  const isDriver = role === "driver";

  const loadRequests = useCallback(async ({ silent = false } = {}) => {
    if (!supabase) {
      setLoadError("setup");
      setLoading(false);
      return;
    }
    if (silent) setRefreshing(true);
    else {
      setLoading(true);
      setLoadError("");
    }
    try {
      const { data, error } = await supabase.rpc(isDriver ? "get_driver_ride_offers" : "get_my_ride_requests");
      if (error) throw error;
      setRequests(data || []);
      setLoadError("");
    } catch (error) {
      console.error("Could not load vehicle trip requests", error);
      setLoadError(isMissingRideSchema(error) ? "setup" : "load");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isDriver]);

  useEffect(() => {
    loadRequests();
    const intervalId = window.setInterval(() => loadRequests({ silent: true }), 5000);
    const onFocus = () => loadRequests({ silent: true });
    window.addEventListener("focus", onFocus);
    const filterColumn = isDriver ? "driver_id" : "client_id";
    const channel = supabase && user?.id
      ? supabase.channel(`ride-requests-${filterColumn}-${user.id}`)
        .on("postgres_changes", {
          event: "*",
          schema: "public",
          table: "ride_requests",
          filter: `${filterColumn}=eq.${user.id}`,
        }, () => loadRequests({ silent: true }))
        .subscribe()
      : null;
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      if (channel) supabase.removeChannel(channel);
    };
  }, [isDriver, loadRequests, user?.id]);

  useEffect(() => {
    if (!isDriver || !supabase) {
      setDriverOnline(false);
      return undefined;
    }
    let active = true;
    const refreshPresence = async () => {
      const { data, error } = await supabase.rpc("get_my_driver_presence");
      if (active && !error) setDriverOnline(data?.[0]?.is_online === true);
    };
    const handlePresenceChange = (event) => setDriverOnline(event.detail?.isOnline === true);
    refreshPresence();
    const intervalId = window.setInterval(refreshPresence, 15000);
    window.addEventListener("uztravel-driver-presence", handlePresenceChange);
    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("uztravel-driver-presence", handlePresenceChange);
    };
  }, [isDriver]);

  const trackingRideKey = isDriver
    ? requests.filter((request) => ["driver_en_route", "passengers_picked_up"].includes(request.status)).map((request) => request.id).sort().join("|")
    : "";

  useEffect(() => {
    if (!trackingRideKey || (isDriver && !driverOnline)) {
      setLocationShareState("idle");
      return undefined;
    }
    if (!navigator.geolocation) {
      setLocationShareState("unavailable");
      return undefined;
    }

    const requestIds = trackingRideKey.split("|");
    let active = true;
    let lastSentAt = 0;
    let updateInFlight = false;
    setLocationShareState("requesting");
    const watchId = navigator.geolocation.watchPosition(async ({ coords }) => {
      const now = Date.now();
      if (!active || updateInFlight || now - lastSentAt < 15000) return;
      updateInFlight = true;
      lastSentAt = now;
      try {
        const results = await Promise.all(requestIds.map((requestId) => supabase.rpc("update_driver_ride_location", {
          p_request_id: requestId,
          p_driver_lat: coords.latitude,
          p_driver_lng: coords.longitude,
        })));
        if (active) setLocationShareState(results.some(({ error, data }) => error || data === false) ? "error" : "sharing");
      } catch (error) {
        console.error("Could not share driver location", error);
        if (active) setLocationShareState("error");
      } finally {
        updateInFlight = false;
      }
    }, (error) => {
      console.warn("Driver location permission unavailable", error);
      if (active) setLocationShareState(error.code === 1 ? "denied" : "error");
    }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });

    return () => {
      active = false;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [driverOnline, isDriver, trackingRideKey]);

  const runAction = async (request, action, etaMinutes = 10) => {
    if (action === "driver_cancel" && !window.confirm(t("rides.confirm_driver_cancel"))) return;
    if (action === "complete" && !window.confirm(t("rides.confirm_complete"))) return;
    setWorkingId(request.id);
    setActionError("");
    try {
      const progressStatuses = {
        start_route: "driver_en_route",
        arrived: "driver_arrived",
        passengers_picked_up: "passengers_picked_up",
        complete: "completed",
      };
      const rpcCall = progressStatuses[action]
        ? supabase.rpc("set_driver_ride_progress", {
          p_request_id: request.id,
          p_next_status: progressStatuses[action],
          p_eta_minutes: action === "start_route" ? etaMinutes : null,
        })
        : action === "cancel"
        ? supabase.rpc("cancel_ride_request", { p_request_id: request.id })
        : action === "driver_cancel"
          ? supabase.rpc("cancel_driver_ride_request", { p_request_id: request.id })
          : supabase.rpc("respond_to_ride_request", {
          p_request_id: request.id,
          p_accept: action === "accept",
        });
      const { data, error } = await rpcCall;
      if (error) throw error;
      if (data === false) throw new Error("request_changed");
      await loadRequests({ silent: true });
    } catch (error) {
      console.error("Could not update vehicle trip request", error);
      const requestWasAlreadyChanged = error?.message === "request_changed"
        || /ride_request_already_answered|ride_request_not_found|invalid_ride_status_transition/.test(error?.message || "");
      setActionError(requestWasAlreadyChanged ? t("rides.already_changed") : t("rides.action_error"));
      await loadRequests({ silent: true });
    } finally {
      setWorkingId(null);
    }
  };

  const heading = historyMode ? t("rides.history_title") : isDriver ? t("rides.driver_title") : t("rides.client_title");
  const displayedRequests = historyMode
    ? [...requests].sort((left, right) => new Date(right.created_at || right.travel_at) - new Date(left.created_at || left.travel_at))
    : requests;

  return (
    <section className={`rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 ${className}`} aria-label={heading}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[.16em] text-amber-600 dark:text-amber-400">{historyMode ? t("rides.history_eyebrow") : isDriver ? t("rides.driver_eyebrow") : t("rides.client_eyebrow")}</p>
          {historyMode ? <h1 className="mt-1 text-xl font-black sm:text-2xl">{heading}</h1> : <h2 className="mt-1 text-xl font-black sm:text-2xl">{heading}</h2>}
        </div>
        <button type="button" onClick={() => loadRequests()} disabled={loading} aria-label={t("rides.refresh")} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-amber-400 hover:text-amber-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300">
          <RefreshCw className={`h-4 w-4 ${refreshing || loading ? "animate-spin" : ""}`} aria-hidden="true" />
        </button>
      </div>

      {actionError && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200">{actionError}</p>}
      {loadError && <div role="alert" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100"><p>{loadError === "setup" ? t("rides.setup_error") : t("rides.load_error")}</p><button type="button" onClick={() => loadRequests()} className="mt-2 font-bold underline underline-offset-2">{t("rides.retry")}</button></div>}

      {loading && !loadError ? <div role="status" className="flex items-center justify-center gap-2 py-10 text-sm font-semibold text-slate-500 dark:text-slate-400"><LoaderCircle className="h-4 w-4 animate-spin text-amber-500" />{t("rides.loading")}</div> : null}
      {!loading && !loadError && displayedRequests.length === 0 ? <p className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm leading-6 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">{isDriver ? t("rides.driver_empty") : historyMode ? t("rides.history_empty") : t("rides.client_empty")}</p> : null}

      {!loading && !loadError && displayedRequests.length > 0 && <div className="mt-4 space-y-3">
        {displayedRequests.map((request) => {
          const busy = workingId === request.id;
          const route = `${t(`tickets.cities.${request.from_city}`, request.from_city)} → ${t(`tickets.cities.${request.to_city}`, request.to_city)}`;
          const statusTone = ["accepted", "driver_en_route", "driver_arrived", "passengers_picked_up", "completed"].includes(request.status)
            ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
            : request.status === "declined" || request.status === "cancelled"
              ? "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200";
          const phone = displayPhone(request.other_party_phone);
          const driverLocationAvailable = Number.isFinite(Number(request.driver_lat))
            && Number.isFinite(Number(request.driver_lng))
            && request.driver_lat != null
            && request.driver_lng != null;
          const statusTime = new Date(request.status_updated_at || request.responded_at || request.created_at).getTime();
          const etaRemaining = request.status === "driver_en_route" && request.driver_eta_minutes
            ? Math.max(0, Number(request.driver_eta_minutes) - Math.floor((Date.now() - statusTime) / 60000))
            : null;
          const vehiclePhotoUrl = request.catalog_photo_path && supabase
            ? supabase.storage.from(CATALOG_PHOTO_BUCKET).getPublicUrl(request.catalog_photo_path).data?.publicUrl || ""
            : "";
          const showDriverDetails = !isDriver && ["accepted", "driver_en_route", "driver_arrived", "passengers_picked_up"].includes(request.status);
          const showClientTracking = !isDriver && ["accepted", "driver_en_route", "driver_arrived", "passengers_picked_up"].includes(request.status);
          const showProgress = !["pending", "declined"].includes(request.status);
          const showContact = ["accepted", "driver_en_route", "driver_arrived", "passengers_picked_up"].includes(request.status);
          const progressDescription = request.status === "driver_en_route"
            ? etaRemaining > 0
              ? t("rides.status_driver_en_route_text", { minutes: etaRemaining })
              : t("rides.status_driver_en_route_late")
            : t(`rides.status_${request.status}_text`, t("rides.status_accepted_text"));

          return (
            <article key={request.id} className="rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50/60 via-white to-white p-4 dark:border-slate-800 dark:from-amber-400/5 dark:via-slate-900 dark:to-slate-900 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="break-words font-extrabold">{route}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"><CalendarDays className="h-3.5 w-3.5 shrink-0 text-amber-500" />{formatTravelAt(request.travel_at, i18n.language)}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-bold ${statusTone}`}>{t(`rides.status_${request.status}`, request.status)}</span>
              </div>

              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /><div className="min-w-0"><p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("rides.pickup")}</p><p className="mt-0.5 break-words font-semibold">{request.pickup_address}, {t(`tickets.cities.${request.from_city}`, request.from_city)}</p><a href={getPickupMapUrl(request)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:underline dark:text-amber-300">{t("rides.open_map")}<ExternalLink className="h-3 w-3" /></a></div></div>
                <div className="flex items-start gap-2.5"><UsersRound className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /><div><p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("rides.passengers_luggage")}</p><p className="mt-0.5 font-semibold">{t("rides.passenger_count", { count: request.passenger_count })} · {t("rides.luggage_count", { count: request.luggage_count })}</p></div></div>
                <div className="flex items-start gap-2.5"><CarFront className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /><div><p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("rides.vehicle")}</p><p className="mt-0.5 font-semibold">{t(`tickets.flow.transport_types.${request.transport_type}.title`)} · {t(`tickets.flow.classes.${request.vehicle_class}.title`)}</p></div></div>
              </div>

              {showClientTracking && <RideTrackingMap request={request} driverLocationAvailable={driverLocationAvailable} statusLabel={t(`rides.status_${request.status}`, request.status)} statusDescription={progressDescription} t={t} />}

              {showProgress && <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-400/20 dark:bg-amber-400/5">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300">
                    {request.status === "driver_en_route" || request.status === "passengers_picked_up" ? <Navigation className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-extrabold">{t(`rides.status_${request.status}`, request.status)}</p>
                    <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">{progressDescription}</p>
                    {!isDriver && request.status === "driver_en_route" && request.driver_eta_minutes && <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 dark:text-amber-200"><Clock3 className="h-3.5 w-3.5" />{etaRemaining > 0 ? t("rides.eta_remaining", { minutes: etaRemaining }) : t("rides.eta_passed")}</p>}
                    {!isDriver && request.status === "driver_en_route" && !driverLocationAvailable && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("rides.driver_location_waiting")}</p>}
                    {isDriver && ["driver_en_route", "passengers_picked_up"].includes(request.status) && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t(`rides.location_sharing_${locationShareState}`, t("rides.location_sharing_requesting"))}</p>}
                    {isDriver && ["driver_en_route", "passengers_picked_up"].includes(request.status) && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t("rides.location_sharing_help")}</p>}
                    {!isDriver && driverLocationAvailable && <>
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("rides.location_updated", { time: formatLocationTime(request.driver_location_updated_at, i18n.language) })}</p>
                      <a href={getPickupMapUrl({ pickup_lat: request.driver_lat, pickup_lng: request.driver_lng })} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:underline dark:text-amber-300">{t("rides.open_driver_location")}<ExternalLink className="h-3 w-3" /></a>
                    </>}
                  </div>
                </div>
              </div>}

              {request.vehicle_name && <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
                {vehiclePhotoUrl && <img src={vehiclePhotoUrl} alt="" className="h-40 w-full object-cover sm:h-48" loading="lazy" />}
                <div className="p-3">
                  <p className="text-xs text-slate-600 dark:text-slate-300">{t("rides.vehicle")}: <span className="font-bold">{request.vehicle_name}</span>{request.vehicle_color ? ` · ${request.vehicle_color}` : ""}</p>
                  {showDriverDetails && <div className="mt-3 flex items-center gap-3 border-t border-slate-100 pt-3 dark:border-slate-800">
                    <ProfileAvatar avatarUrl={request.other_party_avatar_url} name={request.other_party_name} className="h-10 w-10 shrink-0" textClassName="text-xs" />
                    <div className="min-w-0 flex-1"><p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{t("tickets.flow.verified_listing")}</p><p className="truncate text-sm font-bold">{request.other_party_name || "—"}</p></div>
                    {request.vehicle_plate_number && <div aria-label={t("dashboard.driver_verification_plate")} title={t("dashboard.driver_verification_plate")} className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-right dark:border-slate-700 dark:bg-slate-800"><p className="font-mono text-sm font-extrabold tracking-wide">{request.vehicle_plate_number}</p></div>}
                  </div>}
                </div>
              </div>}
              {request.note && <p className="mt-3 whitespace-pre-wrap break-words rounded-xl bg-slate-50 p-3 text-sm leading-6 text-slate-600 dark:bg-slate-800/70 dark:text-slate-300">{request.note}</p>}

              {showContact && request.other_party_phone && <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
                <p className="text-xs font-extrabold uppercase tracking-wider text-emerald-800 dark:text-emerald-200">{isDriver ? t("rides.client_contact") : t("rides.driver_contact")}: {request.other_party_name || request.other_party_phone}</p>
                <div className="mt-3"><a href={`tel:${phone}`} className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 text-sm font-bold text-slate-950 transition hover:bg-amber-400"><Phone className="h-4 w-4" />{request.other_party_phone}</a></div>
              </div>}

              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                {isDriver && request.status === "pending" && <><button type="button" onClick={() => runAction(request, "accept")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-amber-400 px-4 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300 disabled:opacity-60">{busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("rides.accept")}</button><button type="button" onClick={() => runAction(request, "decline")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-bold text-slate-600 transition hover:border-red-300 hover:text-red-600 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300"><X className="h-4 w-4" />{t("rides.decline")}</button></>}
                {isDriver && request.status === "accepted" && <>
                  <label className="flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 px-3 text-xs font-bold dark:border-slate-700">
                    <span>{t("rides.eta_label")}</span>
                    <select aria-label={t("rides.eta_label")} value={etaByRequest[request.id] || "10"} onChange={(event) => setEtaByRequest((current) => ({ ...current, [request.id]: event.target.value }))} className="bg-transparent text-sm outline-none">
                      {[5, 10, 15, 20, 30].map((minutes) => <option key={minutes} value={minutes}>{t("rides.eta_option", { minutes })}</option>)}
                    </select>
                  </label>
                  <button type="button" onClick={() => runAction(request, "start_route", Number(etaByRequest[request.id] || 10))} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-amber-400 px-4 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300 disabled:opacity-60">{busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}{t("rides.driver_start_route")}</button>
                </>}
                {isDriver && request.status === "driver_en_route" && <button type="button" onClick={() => runAction(request, "arrived")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-amber-400 px-4 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300 disabled:opacity-60">{busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}{t("rides.driver_mark_arrived")}</button>}
                {isDriver && request.status === "driver_arrived" && <button type="button" onClick={() => runAction(request, "passengers_picked_up")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-amber-400 px-4 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300 disabled:opacity-60">{busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("rides.driver_mark_picked_up")}</button>}
                {isDriver && request.status === "passengers_picked_up" && <button type="button" onClick={() => runAction(request, "complete")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-extrabold text-white transition hover:bg-emerald-700 disabled:opacity-60">{busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("rides.complete")}</button>}
                {isDriver && ["accepted", "driver_en_route", "driver_arrived"].includes(request.status) && <button type="button" onClick={() => runAction(request, "driver_cancel")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-red-200 px-4 text-sm font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-60 dark:border-red-400/20 dark:text-red-300 dark:hover:bg-red-400/10"><X className="h-4 w-4" />{t("rides.driver_cancel")}</button>}
                {!isDriver && ["pending", "accepted", "driver_en_route", "driver_arrived"].includes(request.status) && <button type="button" onClick={() => runAction(request, "cancel")} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-red-200 px-4 text-sm font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-60 dark:border-red-400/20 dark:text-red-300 dark:hover:bg-red-400/10">{busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}{t("rides.cancel")}</button>}
                {request.status === "pending" && <span className="inline-flex items-center gap-1.5 self-center text-xs font-semibold text-slate-500 dark:text-slate-400"><Clock3 className="h-3.5 w-3.5" />{t(isDriver ? "rides.awaiting_driver" : "rides.awaiting_response")}</span>}
              </div>
            </article>
          );
        })}
      </div>}
    </section>
  );
}
